import { FC } from "react";
import { useEnum } from "@/api/services/global/enum";
import { InnerBox } from "@/modules/irohana-components/elements/InnerBox";
import { Grid } from "@mui/material";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { useFormContext, useWatch } from "react-hook-form";
import { TOrganizationForm } from "@/features/organization/types/organizationForm";
import { Select } from "@/modules/irohana-components/elements/Select";
import { EOrganizationContractStatus } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { Input } from "@/modules/irohana-components/elements/Input";
import { InputDatePicker } from "@/modules/irohana-components/elements/InputDatePicker";
import { EMPTY_STR } from "@/constants/string";

export const ContractForm: FC = () => {
  const methods = useFormContext<TOrganizationForm>();
  const { control, watch, setValue } = methods;

  const contractStatus = watch("contractStatus");
  const { data: contractStatusOptions } = useEnum("EOrganizationContractStatus");

  const trialEndDateWatchField = useWatch({ control, name: "trialEndDate" });
  const trialStartDateWatchField = useWatch({ control, name: "trialStartDate" });
  const contractEndDateWatchField = useWatch({ control, name: "contractEndDate" });
  const contractStartDateWatchField = useWatch({ control, name: "contractStartDate" });

  const handleChangeContractStatus = (value: string) => {
    if (value === EOrganizationContractStatus.TRIAL) {
      if (!trialStartDateWatchField) {
        setValue("trialStartDate", dayjs());
      }
      if (!trialEndDateWatchField) {
        const newDate = dayjs(trialStartDateWatchField || dayjs()).add(2, "weeks");
        setValue("trialEndDate", newDate);
      }
    }
    if (value === EOrganizationContractStatus.DURING_CONTRACT && !contractStartDateWatchField) {
      setValue("contractStartDate", dayjs(), { shouldValidate: true });
    }
  };

  return (
    <InnerBox title="契約情報" p={3} bordered>
      <Grid container rowSpacing={2} columnSpacing={3}>
        <Grid item xs={6}>
          <FormField control={control} name="contractStatus" label="ステータス" required>
            <Select options={contractStatusOptions ?? []} onChange={handleChangeContractStatus} placeholder="選択してください" />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="contractPlan" label="契約プラン">
            <Input placeholder="入力してください" />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="trialStartDate" label="トライアル開始日">
            <InputDatePicker
              placeholder={EMPTY_STR.DATE}
              format="YYYY/MM/DD"
              onChange={(value) => {
                if (!trialEndDateWatchField && !!value) {
                  const newDate = dayjs(value).add(2, "weeks");
                  setValue("trialEndDate", newDate);
                }
              }}
            />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="trialEndDate" label="トライアル終了日">
            <InputDatePicker placeholder={EMPTY_STR.DATE} format="YYYY/MM/DD" />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField
            control={control}
            name="contractStartDate"
            label="契約日"
            required={contractStatus === EOrganizationContractStatus.DURING_CONTRACT}
          >
            <InputDatePicker placeholder={EMPTY_STR.DATE} maxDate={contractEndDateWatchField ?? undefined} format="YYYY/MM/DD" />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="contractEndDate" label="解約日">
            <InputDatePicker placeholder={EMPTY_STR.DATE} format="YYYY/MM/DD" />
          </FormField>
        </Grid>
      </Grid>
    </InnerBox>
  );
};
